import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { View, Text, Platform, TouchableOpacity, ScrollView } from 'react-native';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../config/mapbox-config';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import {
  USA_POI_TILES,
  USA_POI_TILE_COUNT,
  USA_POI_TILE_SET_ID,
  isUsaV1CoverageDoc,
} from '../../data/usaPoiTiles';

/** Static GeoJSON for the full permanent USA grid (under coverage fills). */
const USA_PERMANENT_GRID_FEATURES = USA_POI_TILES.map((tile) => ({
  type: 'Feature',
  id: `usa-grid-${tile.id}`,
  properties: { tileId: tile.id },
  geometry: {
    type: 'Polygon',
    coordinates: [
      [
        [tile.bbox.west, tile.bbox.south],
        [tile.bbox.east, tile.bbox.south],
        [tile.bbox.east, tile.bbox.north],
        [tile.bbox.west, tile.bbox.north],
        [tile.bbox.west, tile.bbox.south],
      ],
    ],
  },
}));

const USA_PERMANENT_GRID_GEOJSON = {
  type: 'FeatureCollection',
  features: USA_PERMANENT_GRID_FEATURES,
};

/** Alaska segments cross the antimeridian; omit from auto–fit-bounds so the map doesn’t zoom to the whole world. */
const USA_PERMANENT_GRID_FEATURES_FOR_BOUNDS = USA_PERMANENT_GRID_FEATURES.filter(
  (f) => !String(f.properties?.tileId || '').startsWith('usa_ak_')
);

function colorForCategory(cat) {
  const s = String(cat || '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = (h >>> 0) % 360;
  return `hsla(${hue}, 65%, 52%, 0.38)`;
}

function bboxToFeature(doc) {
  const b = doc?.bbox;
  if (!b || ![b.south, b.west, b.north, b.east].every((v) => Number.isFinite(v))) return null;
  return {
    type: 'Feature',
    id: doc.id,
    properties: {
      category: doc.category || '',
      tileKey: doc.tileKey || '',
      poiCount: Number(doc.poiCount) || 0,
      fill: colorForCategory(doc.category),
    },
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [b.west, b.south],
          [b.east, b.south],
          [b.east, b.north],
          [b.west, b.north],
          [b.west, b.south],
        ],
      ],
    },
  };
}

function boundsFromFeatures(features) {
  if (!features.length) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  features.forEach((f) => {
    const ring = f.geometry?.coordinates?.[0];
    if (!ring) return;
    ring.forEach(([lng, lat]) => {
      w = Math.min(w, lng);
      e = Math.max(e, lng);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
    });
  });
  if (!Number.isFinite(w)) return null;
  return [
    [w, s],
    [e, n],
  ];
}

/**
 * Web-only: shows poiSeedCoverage tile bboxes as semi-transparent fills.
 */
export function AdminPoiCoverageMap({
  docs = [],
  categoryFilter = 'all',
  onCategoryChange,
  categoryOptions = [],
}) {
  const { colors } = useAdminThemeContext();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const geojsonRef = useRef({ type: 'FeatureCollection', features: [] });
  const [mapHostReady, setMapHostReady] = useState(false);

  const features = useMemo(() => {
    const list = Array.isArray(docs) ? docs : [];
    return list
      .filter((d) => {
        if (categoryFilter === 'all') return true;
        return d.category === categoryFilter;
      })
      .map(bboxToFeature)
      .filter(Boolean);
  }, [docs, categoryFilter]);

  const geojson = useMemo(
    () => ({
      type: 'FeatureCollection',
      features,
    }),
    [features]
  );

  geojsonRef.current = geojson;

  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !MAPBOX_ACCESS_TOKEN || !mapHostReady || !containerRef.current) {
      return undefined;
    }
    if (mapRef.current) return undefined;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-98, 39],
      zoom: 3,
      attributionControl: true,
    });

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    mapRef.current = map;

    const addLayers = () => {
      if (map.getSource('poi-seed-coverage')) return;
      if (!map.getSource('usa-permanent-grid')) {
        map.addSource('usa-permanent-grid', {
          type: 'geojson',
          data: USA_PERMANENT_GRID_GEOJSON,
        });
        map.addLayer({
          id: 'usa-permanent-grid-fill',
          type: 'fill',
          source: 'usa-permanent-grid',
          paint: {
            'fill-color': 'rgba(255,255,255,0.03)',
            'fill-outline-color': 'rgba(255,255,255,0.14)',
          },
        });
      }
      map.addSource('poi-seed-coverage', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'poi-seed-coverage-fill',
        type: 'fill',
        source: 'poi-seed-coverage',
        paint: {
          'fill-color': ['get', 'fill'],
          'fill-outline-color': 'rgba(255,255,255,0.55)',
        },
      });
      const src = map.getSource('poi-seed-coverage');
      if (src && typeof src.setData === 'function') {
        src.setData(geojsonRef.current);
      }
    };

    map.on('load', addLayers);
    if (map.loaded()) addLayers();

    return () => {
      try {
        map.remove();
      } catch (_) {
        /* ignore */
      }
      mapRef.current = null;
    };
  }, [mapHostReady]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !mapRef.current) return;
    const map = mapRef.current;
    if (!map.loaded()) return;
    const src = map.getSource('poi-seed-coverage');
    if (src && typeof src.setData === 'function') {
      src.setData(geojson);
    }
    const coverageForBounds = features.filter(
      (f) => !String(f.properties?.tileKey || '').startsWith('usa_ak_')
    );
    const b = boundsFromFeatures([...USA_PERMANENT_GRID_FEATURES_FOR_BOUNDS, ...coverageForBounds]);
    if (b) {
      try {
        map.fitBounds(b, { padding: 48, maxZoom: 10, duration: 600 });
      } catch (_) {
        /* ignore */
      }
    }
  }, [geojson, features]);

  if (Platform.OS !== 'web') {
    return (
      <View style={{ padding: 12, backgroundColor: colors.cardBgSubtle, borderRadius: 10, borderWidth: 1, borderColor: colors.cardBorder }}>
        <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
          Seed coverage map is available on web Admin only.
        </Text>
      </View>
    );
  }

  if (!MAPBOX_ACCESS_TOKEN) {
    return (
      <View style={{ padding: 12 }}>
        <Text style={{ color: '#e67e22', fontSize: 12 }}>Mapbox token missing — cannot show coverage map.</Text>
      </View>
    );
  }

  return (
    <View style={{ marginTop: 12 }}>
      {typeof onCategoryChange === 'function' && categoryOptions.length > 0 ? (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ color: colors.textSecondary, fontSize: 11, marginBottom: 6 }}>Highlight category</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <TouchableOpacity
              onPress={() => onCategoryChange('all')}
              style={{
                paddingVertical: 6,
                paddingHorizontal: 12,
                borderRadius: 999,
                marginRight: 8,
                backgroundColor: categoryFilter === 'all' ? '#e67e22' : colors.cardBgSubtle,
                borderWidth: 1,
                borderColor: categoryFilter === 'all' ? '#e67e22' : colors.cardBorder,
              }}
            >
              <Text style={{ color: categoryFilter === 'all' ? '#fff' : colors.textPrimary, fontWeight: '700', fontSize: 11 }}>All</Text>
            </TouchableOpacity>
            {categoryOptions.map((opt) => {
              const active = categoryFilter === opt.key;
              return (
                <TouchableOpacity
                  key={opt.key}
                  onPress={() => onCategoryChange(opt.key)}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 999,
                    marginRight: 8,
                    backgroundColor: active ? '#e67e22' : colors.cardBgSubtle,
                    borderWidth: 1,
                    borderColor: active ? '#e67e22' : colors.cardBorder,
                  }}
                >
                  <Text style={{ color: active ? '#fff' : colors.textPrimary, fontWeight: '700', fontSize: 10 }} numberOfLines={1}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      ) : null}

      <div
        ref={(el) => {
          containerRef.current = el;
          setMapHostReady(!!el);
        }}
        style={{
          width: '100%',
          height: 320,
          borderRadius: 12,
          overflow: 'hidden',
          border: `1px solid ${colors.cardBorder}`,
        }}
      />

      <Text style={{ color: colors.textMuted, fontSize: 10, marginTop: 6 }}>
        {USA_POI_TILE_SET_ID}: {USA_PERMANENT_GRID_FEATURES.length} permanent cells · {features.length} coverage tile
        {features.length === 1 ? '' : 's'} shown
        {categoryFilter !== 'all' ? ` · ${categoryFilter}` : ''}
      </Text>
    </View>
  );
}
